import { makeStyles, shorthands } from "@fluentui/react-components";
import { designTokens } from "../../../../theme/customTheme";

export const useWorkspaceManageContentStyles = makeStyles({
  modalContentOverride: {
    maxWidth: "900px",
    width: "90%",
    height: "80vh",
    ...shorthands.overflow("hidden"),
    "@media (max-width: 640px)": {
      maxWidth: "100vw",
      width: "100%",
      height: "95vh",
      borderRadius: 0,
    },
  },

  modalHeaderOverride: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  tabNavigation: {
    position: "relative",
    ...shorthands.borderBottom("1px", "solid", designTokens.neutral[200]),
    display: "flex",
    alignItems: "center",
  },

  tabListContainer: {
    overflowX: "auto",
    overflowY: "hidden",
    scrollbarWidth: "none",
    flex: 1,
    "::-webkit-scrollbar": {
      display: "none",
    },
  },

  tabListContainerOverflow: {
    marginLeft: "36px",
    marginRight: "36px",
  },

  tabList: {
    padding: `0 ${designTokens.spacing[4]}`,
    whiteSpace: "nowrap",
    minWidth: "fit-content",
    ...shorthands.borderBottom("1px", "solid", designTokens.neutral[200]),
  },

  tab: {
    "&::after": {
      width: "100%",
      left: 0,
      right: 0,
    },
  },

  tabContent: {
    display: "flex",
    ...shorthands.gap(designTokens.spacing[1]),
    alignItems: "center",
    whiteSpace: "nowrap",
    flex: 1,
    ...shorthands.overflow("auto"),
  },

  tabSpacer: {
    flex: 1,
  },

  tabContentArea: {
    flex: 1,
    ...shorthands.overflow("auto"),
    padding: designTokens.spacing[5],
    paddingRight: designTokens.spacing[6],
  },

  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: designTokens.spacing[10],
  },

  selectedTagIcon: {
    width: designTokens.fontSize.sm,
    height: designTokens.fontSize.sm,
  },

  selectedTagIconLarge: {
    width: designTokens.fontSize.base,
    height: designTokens.fontSize.base,
  },

  selectedTagIconSmall: {
    width: designTokens.fontSize.xs,
    height: designTokens.fontSize.xs,
  },

  removeTagIcon: {
    width: designTokens.iconSize.sm,
    height: designTokens.iconSize.sm,
  },

  documentItemContent: {
    display: "flex",
    ...shorthands.gap(designTokens.spacing[2]),
    alignItems: "center",
  },

  documentIconLarge: {
    width: "24px",
    height: "24px",
    borderRadius: designTokens.borderRadius.sm,
    padding: designTokens.spacing[1],
  },

  documentDetails: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  conversationIcon: {
    width: "20px",
    height: "20px",
    padding: designTokens.spacing[2],
    borderRadius: designTokens.borderRadius.md,
  },

  conversationItemDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },

  addIconDisabled: {
    opacity: 0.3,
  },

  tabBadgeMargin: {
    marginLeft: designTokens.spacing[2],
  },

  section: {
    marginBottom: designTokens.spacing[6],
  },

  sectionHeader: {
    justifyContent: "space-between",
  },

  sectionTitle: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[700],
    marginBottom: designTokens.spacing[3],
  },

  selectedItemsContainer: {
    display: "flex",
    flexWrap: "wrap",
    ...shorthands.gap(designTokens.spacing[2]),
    marginBottom: designTokens.spacing[4],
    minHeight: "32px",
    padding: designTokens.spacing[3],
    borderRadius: designTokens.borderRadius.md,
  },

  emptySelectedState: {
    color: designTokens.neutral[500],
    fontSize: designTokens.fontSize.sm,
    fontStyle: "italic",
    alignSelf: "center",
  },

  removeButton: {
    width: designTokens.iconSize.md,
    height: designTokens.iconSize.md,
    minWidth: designTokens.iconSize.md,
  },

  visuallyHidden: {
    position: "absolute",
    width: "1px",
    height: "1px",
    ...shorthands.overflow("hidden"),
    whiteSpace: "nowrap",
    clip: "rect(0,0,0,0)",
  },

  availableItemsList: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(designTokens.spacing[2]),
  },

  availableItemsListForChat: {
    display: "flex",
    ...shorthands.gap(designTokens.spacing[2]),
  },

  accordionContainer: {
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    borderRadius: designTokens.borderRadius.md,
  },

  accordionHeaderContent: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(designTokens.spacing[2]),
  },

  accordionHeader: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    padding: designTokens.spacing[3],
  },

  accordionPanel: {
    padding: designTokens.spacing[3],
    paddingTop: 0,
  },

  selectableItem: {
    display: "flex",
    alignItems: "center",
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    borderRadius: designTokens.borderRadius.md,
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    ":hover": {
      backgroundColor: designTokens.neutral[100],
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
    ...shorthands.gap(designTokens.spacing[5]),
    backgroundColor: designTokens.neutral.white,
  },

  selectableItemSelected: {
    backgroundColor: designTokens.accent[50],
    ...shorthands.border("1px", "solid", designTokens.accent[200]),
  },

  selectableItemContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap(designTokens.spacing[2]),
    flex: 1,
  },

  selectableItemText: {
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[900],
  },

  documentInfo: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(designTokens.spacing[2]),
    flex: 1,
  },

  addButton: {
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[5]}`,
  },

  conversationItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap(designTokens.spacing[2]),
    padding: designTokens.spacing[3],
    cursor: "pointer",
    ":hover": {
      backgroundColor: designTokens.neutral[100],
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },

  conversationItemSelected: {
    backgroundColor: designTokens.accent[50],
    ...shorthands.border("1px", "solid", designTokens.accent[200]),
  },

  conversationInfo: {
    flex: 1,
  },

  conversationTitle: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    color: designTokens.neutral[900],
    marginBottom: designTokens.spacing[1],
  },

  conversationLastMessage: {
    ...shorthands.overflow("hidden"),
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "300px",
  },

  tabScrollButton: {
    position: "absolute",
    right: 0,
    zIndex: 1,
    height: "100%",
    borderRadius: "0",
    backgroundColor: designTokens.neutral.white,
    ...shorthands.border("none"),
    minWidth: "32px",
    color: designTokens.neutral[600],
    ":hover": {
      backgroundColor: designTokens.neutral[50],
      color: designTokens.neutral[900],
    },
  },

  tabScrollButtonLeft: {
    left: 0,
    right: "auto",
  },

  doneButton: {
    minWidth: "80px",
    fontFamily: designTokens.fontFamily,
    fontWeight: designTokens.fontWeight.semibold,
    fontSize: designTokens.fontSize.sm,
  },

  checkedMark: {
    color: designTokens.accent[600],
    minWidth: "14px",
    minHeight: "14px",
    backgroundColor: designTokens.accent[100],
    borderRadius: designTokens.borderRadius.sm,
    padding: designTokens.spacing[1],
  },

  addIcon: {
    minWidth: "14px",
    minHeight: "14px",
    color: designTokens.neutral[500],
    transition: `color ${designTokens.transition.fast}`,
    ":hover": {
      color: designTokens.accent[600],
    },
  },

  tagNameWithAttachments: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("4px"),
  },

  descriptionBox: {
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    ...shorthands.borderLeft("3px", "solid", designTokens.accent[500]),
    backgroundColor: designTokens.neutral[50],
    borderRadius: designTokens.borderRadius.md,
    padding: designTokens.spacing[4],
    marginBottom: designTokens.spacing[5],
  },

  descriptionBoxText: {
    margin: "0",
    fontSize: designTokens.fontSize.xs,
    lineHeight: "1.55",
    color: designTokens.neutral[600],
    marginBottom: designTokens.spacing[2],
    ":last-child": { marginBottom: "0" },
  },

  descriptionBoxLede: {
    color: designTokens.neutral[900],
  },

  focusTray: {
    display: "flex",
    flexWrap: "wrap",
    ...shorthands.gap(designTokens.spacing[2]),
    minHeight: "44px",
    marginBottom: designTokens.spacing[5],
  },

  focusChipBase: {
    display: "inline-flex",
    alignItems: "center",
    ...shorthands.gap(designTokens.spacing[2]),
    borderRadius: "999px",
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.medium,
  },

  focusChipWorkspace: {
    ...shorthands.border("1px", "solid", designTokens.accent[400]),
    backgroundColor: designTokens.accent[50],
    color: designTokens.accent[700],
  },

  focusChipUploaded: {
    ...shorthands.border("1px", "solid", designTokens.semantic.warningBorder),
    backgroundColor: designTokens.semantic.warningBackground,
    color: designTokens.semantic.warningText,
  },

  chipTypeBadge: {
    fontSize: "9px",
    fontWeight: designTokens.fontWeight.semibold,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    borderRadius: "999px",
    padding: `2px ${designTokens.spacing[2]}`,
    whiteSpace: "nowrap",
  },

  chipTypeBadgeWorkspace: {
    color: designTokens.accent[700],
    backgroundColor: designTokens.accent[100],
  },

  chipTypeBadgeUploaded: {
    color: designTokens.semantic.warningText,
    backgroundColor: designTokens.semantic.warningBackgroundStrong,
  },

  legend: {
    display: "flex",
    ...shorthands.gap(designTokens.spacing[4]),
    flexWrap: "wrap",
    marginBottom: designTokens.spacing[3],
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
  },

  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    ...shorthands.gap(designTokens.spacing[1]),
  },

  legendSwatchTeal: {
    width: "12px",
    height: "12px",
    borderRadius: "3px",
    ...shorthands.border("1px", "solid", designTokens.accent[300]),
    backgroundColor: designTokens.accent[50],
    flexShrink: "0",
  },

  legendSwatchAmber: {
    width: "12px",
    height: "12px",
    borderRadius: "3px",
    ...shorthands.border("1px", "solid", designTokens.semantic.warningBorder),
    backgroundColor: designTokens.semantic.warningBackground,
    flexShrink: "0",
  },

  docRowButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap(designTokens.spacing[3]),
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    borderRadius: designTokens.borderRadius.md,
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    backgroundColor: designTokens.neutral.white,
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    ":hover": {
      backgroundColor: designTokens.neutral[100],
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
});
